import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, X, ArrowRight, Check, Zap, Clock, LifeBuoy, Calendar, BarChart2, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

function StripeCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, refreshSubscriptionStatus, isAuthenticated, needsSubscription } = useAuth();

  // Handle scroll for navbar effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for success parameter from Stripe redirect
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      // Refresh subscription status and then navigate to portal
      refreshSubscriptionStatus();
      navigate('/portal');
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again or contact support.');
    }
  }, [searchParams, navigate, refreshSubscriptionStatus]);

  // Redirect if user doesn't need subscription
  useEffect(() => {
    if (isAuthenticated && !needsSubscription) {
      navigate('/portal');
    }
  }, [isAuthenticated, needsSubscription, navigate]);

  const initiateCheckout = async (tierType) => {
    if (!tierType) {
      setError('Please select a subscription tier');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(process.env.REACT_APP_API_URL + '/product/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tierType: tierType
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create checkout session: ${response.status}`);
      }

      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from server');
      }

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to initiate checkout');
    } finally {
      setLoading(false);
    }
  };

  const featureItems = {
    starter: [
      { icon: <Check className="text-green-500" size={18} />, text: "Basic tutoring tools" },
      { icon: <Check className="text-green-500" size={18} />, text: "Student management dashboard" },
      { icon: <Check className="text-green-500" size={18} />, text: "Scheduling integration" },
      { icon: <Check className="text-green-500" size={18} />, text: "Email support" }
    ],
    pro: [
      { icon: <Zap className="text-blue-500" size={18} />, text: "All Starter features" },
      { icon: <BarChart2 className="text-blue-500" size={18} />, text: "Advanced analytics" },
      { icon: <Calendar className="text-blue-500" size={18} />, text: "Premium calendar features" },
      { icon: <CreditCard className="text-blue-500" size={18} />, text: "Payment processing" },
      { icon: <LifeBuoy className="text-blue-500" size={18} />, text: "Priority customer support" }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50 text-gray-800 font-sans antialiased">
      {/* Navigation */}
      <nav className={`w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white shadow-lg py-2' : 'bg-white/90 backdrop-blur-sm py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <motion.div 
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  className="relative"
                >
                  <BookOpen className="h-8 w-8 text-blue-600" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-pulse"></div>
                </motion.div>
                <span className="ml-2 text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Verve Tutor
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
              >
                Logout
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <div className="text-center">
              <h1 className="text-3xl font-bold">Choose Your Plan</h1>
              <p className="mt-2 opacity-90">Select the subscription that fits your tutoring needs</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Pricing Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Starter Tier */}
              <motion.div 
                whileHover={{ scale: 1.01 }}
                className={`bg-white rounded-lg border-2 ${
                  selectedTier === 'starter' ? 'border-blue-500 shadow-lg' : 'border-gray-200'
                } p-6 cursor-pointer transition-all`}
                onClick={() => setSelectedTier('starter')}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Starter</h3>
                    <p className="text-gray-600">Perfect for new tutors</p>
                  </div>
                  <div className="bg-blue-100 text-blue-800 rounded-full px-4 py-1">
                    <span className="font-bold">$8.99</span>/month
                  </div>
                </div>
                <ul className="mt-6 space-y-3">
                  {featureItems.starter.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-3 mt-0.5">{item.icon}</span>
                      <span className="text-gray-700">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Pro Tier */}
              <motion.div 
                whileHover={{ scale: 1.01 }}
                className={`bg-white rounded-lg border-2 ${
                  selectedTier === 'pro' ? 'border-indigo-500 shadow-lg' : 'border-gray-200'
                } p-6 cursor-pointer transition-all relative`}
                onClick={() => setSelectedTier('pro')}
              >
                <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  POPULAR
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Professional</h3>
                    <p className="text-gray-600">For growing tutoring businesses</p>
                  </div>
                  <div className="bg-indigo-100 text-indigo-800 rounded-full px-4 py-1">
                    <span className="font-bold">$14.99</span>/month
                  </div>
                </div>
                <ul className="mt-6 space-y-3">
                  {featureItems.pro.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-3 mt-0.5">{item.icon}</span>
                      <span className="text-gray-700">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start"
              >
                <X className="text-red-500 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <div>
                  <p className="font-medium">{error}</p>
                  <button 
                    onClick={() => setError(null)} 
                    className="text-xs text-red-600 hover:text-red-800 mt-1 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </motion.div>
            )}

            <div className="flex flex-col space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => initiateCheckout(selectedTier)}
                disabled={loading || !selectedTier}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-white transition-all ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : !selectedTier
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <span>{selectedTier ? `Subscribe to ${selectedTier === 'pro' ? 'Professional' : 'Starter'} Plan` : 'Select a Plan'}</span>
                    {selectedTier && <ArrowRight className="ml-2" size={18} />}
                  </div>
                )}
              </motion.button>

              <div className="flex items-center justify-center text-xs text-gray-500">
                <Clock className="mr-1" size={14} />
                <span>Secure checkout powered by Stripe</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <button 
                onClick={() => navigate('/portal')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                Already subscribed? Access your Tutor Portal
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default StripeCheckout;